/**
 * The questions the ICD wizards answer, apart from anything that draws them
 * (issue #52).
 *
 * Each one is a place a wrong answer renders something plausible: flows drawn
 * the wrong way, a pin list off by one, a "mating side" that is the same side.
 */

import type { EntityRow, InterfaceFeature, InterfaceTypeRow } from '../../schema'
import { effectiveDirection } from '../parts/interfaces-model'

/** The three disciplines, in the order an ICD is usually split. */
export const DISCIPLINES = ['Software', 'Electrical', 'Mechanical'] as const

/** The program's own types, grouped under each discipline they reach. */
export function typesByDiscipline(
  types: readonly InterfaceTypeRow[],
): Record<string, InterfaceTypeRow[]> {
  const grouped: Record<string, InterfaceTypeRow[]> = {}
  for (const discipline of DISCIPLINES) grouped[discipline] = []
  for (const type of types) {
    for (const discipline of type.disciplines) {
      ;(grouped[discipline] ??= []).push(type)
    }
  }
  return grouped
}

/**
 * Pin names from what somebody typed: a count ("8") or names, one per line or
 * comma-separated ("A1, A2, B1").
 *
 * A count gives `pin1`..`pinN`, which is what the server writes for one too - so
 * the preview and the file agree. Returns `[]` for nothing, and a message rather
 * than a list for something that cannot be pins.
 */
export function parsePins(text: string): { pins: string[]; problem: string } {
  const trimmed = text.trim()
  if (!trimmed) return { pins: [], problem: '' }
  if (/^\d+$/.test(trimmed)) {
    const count = Number(trimmed)
    if (count < 1 || count > 512) return { pins: [], problem: 'A connector has 1 to 512 pins.' }
    return { pins: Array.from({ length: count }, (_, index) => `pin${index + 1}`), problem: '' }
  }
  const pins = trimmed.split(/[\n,]+/).map((entry) => entry.trim()).filter(Boolean)
  const bad = pins.find((pin) => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(pin))
  if (bad) {
    return {
      pins: [],
      problem: `'${bad}' cannot be a pin name: letters, digits and _ only, not starting with a digit. ` +
        "A numbered pin reads 'pin3' or 'A3'.",
    }
  }
  if (new Set(pins).size !== pins.length) return { pins: [], problem: 'Two pins share a name.' }
  return { pins, problem: '' }
}

/** A port def row's features, when the listing carried them; else none. */
export function featuresOf(row: EntityRow | undefined): InterfaceFeature[] {
  const found = (row as unknown as { features?: InterfaceFeature[] } | undefined)?.features
  return Array.isArray(found) ? found : []
}

/**
 * The flows an interface between two ends implies, from what their port defs
 * declare.
 *
 * For each directed feature on end A's port def, seen from A (so a conjugated A
 * has it reversed): something going OUT of A flows `a.x -> b.x`, something coming
 * IN flows `b.x -> a.x`. Only where end B's port def has a feature of the same
 * name - a flow names the same feature at both ends, and one that is missing on
 * B is a mismatch to fix, not a flow to write.
 */
export function suggestedFlows(
  first: { name: string; features: readonly InterfaceFeature[]; conjugate: boolean },
  second: { name: string; features: readonly InterfaceFeature[] },
): { source: string; target: string }[] {
  const onSecond = new Set(second.features.map((feature) => feature.name))
  const flows: { source: string; target: string }[] = []
  for (const feature of first.features) {
    if (!feature.direction || feature.direction === 'inout' || !onSecond.has(feature.name)) continue
    const seen = effectiveDirection(feature.direction, first.conjugate)
    const a = `${first.name}.${feature.name}`
    const b = `${second.name}.${feature.name}`
    flows.push(seen === 'out' ? { source: a, target: b } : { source: b, target: a })
  }
  return flows
}

/**
 * Whether one port may sit on one interface end - the same rule the server
 * applies, so the Connect dialog only offers ports it will accept.
 *
 * By the TAIL of the type name, and conjugation must agree. Specialization is
 * the server's to judge (it can walk the chain); here an unmatched tail is
 * still offered when `lenient`, so a specialized port is not hidden.
 */
export function portFitsEnd(
  port: { type_name: string; is_conjugate: boolean },
  end: { type_name: string; is_conjugate: boolean },
): boolean {
  if (Boolean(port.is_conjugate) !== Boolean(end.is_conjugate)) return false
  if (!end.type_name || !port.type_name) return true
  return tail(port.type_name) === tail(end.type_name)
}

/** The last segment of a qualified name, quotes dropped. */
export function tail(name: string): string {
  return name.replace(/'/g, '').split('::').pop() ?? name
}

/**
 * The SysML line an interface usage will write, for a preview. Mirrors
 * services/interfaces.connect_ports: the def's qualified name as the type, and
 * each end bound to the def's end name when it has one.
 */
export function interfaceLine(options: {
  name: string
  identifier: string
  typeName: string
  ends: { path: string; endName: string }[]
}): string {
  const id = options.identifier ? `<${options.identifier}> ` : ''
  const name = options.name ? `${options.name} ` : ''
  const type = options.typeName ? `: ${options.typeName} ` : ''
  const ends = options.ends
    .map((end) => (end.endName ? `${end.endName} ::> ${end.path}` : end.path))
    .join(' to ')
  return `interface ${id}${name}${type}connect ${ends};`
}
